import Grid from "@mui/material/Grid2";
import React, { forwardRef } from "react";
import { CommonError } from "@sinch/components/CommonError/CommonError";
import { useMultilevelSelect } from "@sinch/components/form/MultilevelSelect/hooks/useMultilevelSelect";
import { ListColumn } from "@sinch/components/form/MultilevelSelect/ListColumn";
import { ListSearchField } from "@sinch/components/form/MultilevelSelect/ListSearchField";
import { NestedOptions } from "@sinch/components/form/MultilevelSelect/types";

interface MultilevelSelectProps<TValue = any> {
  value: TValue | null;
  onChange?: (e: React.MouseEvent<HTMLLIElement>, value: TValue) => void;
  options: NestedOptions<TValue>[];
  getListItemProps?: (value: TValue) => any;
  enterListEvent?: "click" | "mouseOver";
  onMouseOver?: (index: number[]) => void;
}

const MultilevelSelectBase = <TValue,>(
  {
    value,
    onChange,
    options,
    getListItemProps,
    enterListEvent = "mouseOver",
    onMouseOver,
    ...props
  }: MultilevelSelectProps<TValue>,
  ref: React.Ref<HTMLDivElement>
) => {
  const { onSearch, search, listColumn, loading, selectedPath, error } = useMultilevelSelect<TValue>({
    options,
    value,
    onChange,
  });

  if (error) {
    return <CommonError />;
  }

  return (
    <Grid ref={ref} container direction="column" sx={{ height: "100%" }} wrap="nowrap" {...props}>
      <Grid>
        <ListSearchField loading={loading} onChange={onSearch} value={search} />
      </Grid>
      <Grid container direction="row" sx={{ overflow: "hidden", height: "100%" }}>
        {listColumn.map(({ level, list, onOpenList, onChange: handleChange, selected }) => (
          <ListColumn<TValue>
            key={`level-${level}`}
            getListItemProps={getListItemProps}
            list={list}
            onChange={handleChange}
            onClick={(index: number) => {
              if (enterListEvent === "click") {
                onOpenList(index);
              }
            }}
            onMouseOver={(index: number) => {
              onMouseOver?.(selectedPath);
              if (enterListEvent === "mouseOver") {
                onOpenList(index);
              }
            }}
            selectedIndex={selected}
            selectedValue={value}
          />
        ))}
      </Grid>
    </Grid>
  );
};

export const MultilevelSelect = forwardRef(MultilevelSelectBase) as <TValue>(
  props: MultilevelSelectProps<TValue> & { ref?: React.Ref<HTMLDivElement> }
) => JSX.Element;
